import { parseZodErrorMessages } from "@@/backoffice/dashboard/shared/parse-zod-error-messages";
import { browserLogger } from "@@/settings";
import type { FormId } from "@@/shared/form/form-and-field-id";
import type { UseMutationResult } from "@tanstack/react-query";
import {
    emptyArrayOf,
    emptyObjectOf,
    generateId,
    isFunction,
    parseSafely,
    toTranslatableString,
} from "@towni/common";
import { produce } from "immer";
import objectHash from "object-hash";
import { z, ZodError, type ZodSchema } from "zod";
import { create } from "zustand";
import type { Form3ErrorMessage } from "./form3.error-message";
import type { TForm3 } from "./form3.form";
import { getSchemaAtPath } from "./get-schema-at-path";
import { getFormValueAtPath, setFormValueAtPath } from "./get-value-at-path";

const nullReplacer = (value: unknown) => {
    if (value === null) return undefined;
    return value;
};
const hasher = (object: Parameters<typeof objectHash>[0]) => {
    const hash = objectHash(object, {
        replacer: nullReplacer,
    });
    browserLogger.info("hasher", {
        object,
        hash,
    });
    return hash;
};

// const shallowCompareArray = <T,>(a: T[], b: T[]): boolean => {
//     if (a.length !== b.length) return false;
//     for (let i = 0; i < a.length; i++) {
//         if (a[i] !== b[i]) return false;
//     }
//     return true;
// };
// // eslint-disable-next-line @typescript-eslint/no-explicit-any
// const fieldValueCache = new Map<string, any>();

type InitializeFormParams<
    FormSchema extends ZodSchema,
    FormData extends Record<string, unknown>,
> = {
    readonly formId?: FormId;
    readonly schema: FormSchema;
    readonly initialData: Partial<FormData>;
    readonly clearOnUnmount?: boolean;
    readonly mutation?: UseMutationResult<unknown, unknown, FormData>;
};

type FormStore = {
    forms: Record<FormId, TForm3<ZodSchema, Record<string, unknown>, string>>;
    getForm: <
        FormSchema extends ZodSchema,
        FormData extends Record<string, unknown>,
    >(
        formId: FormId,
    ) => TForm3<FormSchema, FormData, string> | undefined;
    getFormHash: (formId: FormId) => string | undefined;
    initializeForm: <
        FormSchema extends ZodSchema,
        FormData extends Record<string, unknown>,
    >(
        params: InitializeFormParams<FormSchema, FormData>,
    ) => TForm3<FormSchema, FormData, string>;
    deleteForm: (formId: FormId) => void;
};

/**
 * Zustand store for managing forms.
 *
 * @typedef {Object} FormStore - The shape of the form store.
 * @property {Object} forms - A dictionary of forms indexed by form ID.
 * @property {Function} getForm - Retrieves a form by its ID.
 * @property {Function} getFormHash - Retrieves the current hash of a form by its ID.
 * @property {Function} deleteForm - Deletes a form by its ID.
 * @property {Function} initializeForm - Initializes a new form with the given parameters.
 *
 * @typedef {Object} Form - The shape of a form.
 * @property {string} _id - The ID of the form.
 * @property {Object} data - The current data of the form.
 * @property {Object} initialData - The initial data of the form.
 * @property {Object} schema - The Zod schema of the form.
 * @property {Object} touched - A record of touched fields.
 * @property {Object} dirty - A record of dirty fields.
 * @property {string} initialHash - The initial hash of the form data.
 * @property {boolean} clearOnUnmount - Whether to clear the form on unmount.
 * @property {Function} parse - Parses the form data using the schema.
 * @property {Function} safeParse - Safely parses the form data using the schema.
 * @property {Function} parseSafely - Parses the form data safely.
 * @property {Function} calculateHash - Calculates the hash of the given data.
 * @property {Function} calculateFieldHash - Calculates the hash of a specific field.
 * @property {Function} isFieldChanged - Checks if a specific field has changed.
 * @property {string} currentHash - The current hash of the form data.
 * @property {boolean} hasChanges - Whether the form has changes.
 * @property {Function} setData - Sets the form data.
 * @property {Function} setFormField - Sets the value of a specific form field.
 * @property {Function} getFieldValue - Retrieves the value of a specific form field.
 * @property {Function} getFieldSchema - Retrieves the schema of a specific form field.
 * @property {Function} validate - Validates the form data.
 * @property {Function} validateField - Validates a specific form field.
 * @property {Function} resetToInitialState - Resets the form to its initial state.
 *
 * @function useFormStore
 * @description Zustand store for managing forms.
 * @param {Function} set - Zustand set function.
 * @param {Function} get - Zustand get function.
 * @returns {FormStore} The form store.
 */
const useFormStore = create<FormStore>((set, get) => ({
    forms: {},
    getForm: <
        FormSchema extends ZodSchema,
        FormData extends Record<string, unknown>,
    >(
        formId: FormId,
    ): TForm3<FormSchema, FormData, string> | undefined => {
        const form = get().forms[formId] as TForm3<
            FormSchema,
            FormData,
            string
        >;
        if (!form) return undefined;
        return form;
    },
    getFormHash: (formId: FormId) => {
        const form = get().getForm(formId);
        if (!form) return undefined;
        return form.currentHash;
    },
    deleteForm: (formId: FormId) => {
        set(state => {
            return produce(state, draft => {
                browserLogger.info("deleteForm", formId);
                delete draft.forms[formId];
            });
        });
    },
    initializeForm: <
        FormSchema extends ZodSchema,
        FormData extends Record<string, unknown>,
        _FieldId extends string,
    >(
        params: InitializeFormParams<FormSchema, FormData>,
    ) => {
        browserLogger.info("initializeForm", params);
        const { formId: _formId, schema, initialData } = params;
        const formId = _formId ?? generateId<FormId>({ prefix: "form__" });
        const initialHash = hasher(initialData);
        const form: TForm3<FormSchema, FormData, string> = {
            _id: formId,
            data: initialData,
            initialData: initialData,
            schema,
            touched: emptyObjectOf<{ [k: string]: boolean }>(),
            dirty: emptyObjectOf<{ [k: string]: boolean }>(),
            initialHash,
            mutation: params.mutation,
            clearOnUnmount: !!params.clearOnUnmount,
            parse: () => {
                const _form = get().getForm<FormSchema, FormData>(formId);
                if (!_form) throw new Error(`Form "${formId}" does not exist`);
                return _form.schema.parse(_form.data);
            },
            safeParse: () => {
                const _form = get().getForm<FormSchema, FormData>(formId);
                if (!_form) {
                    browserLogger.warn(
                        `Form safeParse; Warning form "${formId}" does not exist`,
                    );
                }
                return _form
                    ? _form.schema.safeParse(_form.data)
                    : {
                          success: false,
                          error: new ZodError([
                              {
                                  code: z.ZodIssueCode.custom,
                                  message: toTranslatableString({
                                      sv: `Kunde inte hitta formulär med id: "${formId}"`,
                                      en: `Could not find form with id: "${formId}"`,
                                  }),
                                  path: ["forms", formId],
                                  params: { formId },
                              },
                          ]),
                      };
            },
            parseSafely: () => {
                const _form = get().getForm<FormSchema, FormData>(formId);
                if (!_form) {
                    browserLogger.warn(
                        `Form safeParse; Warning form "${formId}" does not exist`,
                    );
                    return undefined;
                }
                const value = parseSafely({
                    schema: _form.schema,
                    value: _form.data,
                });
                return value;
            },
            calculateHash: (data: FormData) => hasher(data),
            calculateFieldHash: <FieldId extends string>(fieldId: FieldId) => {
                const form = get().getForm<FormSchema, FormData>(formId);
                if (!form) throw new Error(`Form with id ${formId} not found`);
                const fieldData = form.getFieldValue(fieldId);
                if (!fieldData) throw new Error("Value to hash is undefined");
                return hasher(fieldData);
            },
            isFieldChanged: <FieldId extends string>(fieldId: FieldId) => {
                const form = get().getForm<FormSchema, FormData>(formId);
                if (!form) throw new Error(`Form with id ${formId} not found`);
                const fieldData = form.getFieldValue(fieldId);
                const initialFieldData = form.initialData[fieldId];
                return fieldData !== initialFieldData;
            },
            currentHash: initialHash,
            hasChanges: false,
            setHash: hash => {
                set(state => {
                    return produce(state, draft => {
                        draft.forms[formId].currentHash = hash;
                        if (hash !== state.forms[formId].initialHash) {
                            state.forms[formId].hasChanges = true;
                        }
                    });
                });
            },
            setDirty: (fieldId, value) => {
                set(state => {
                    return produce(state, draft => {
                        draft.forms[formId].dirty[fieldId] = value;
                    });
                });
            },
            setTouched: (fieldId, value) => {
                set(state => {
                    return produce(state, draft => {
                        draft.forms[formId].touched[fieldId] = value;
                    });
                });
            },
            setData: formData => {
                set(state => {
                    const form = state.forms[formId];
                    const data = formData;
                    browserLogger.info("setData hash");
                    const currentHash = hasher(data);
                    form.data = data;
                    form.currentHash = currentHash;
                    const hasChanges = currentHash !== form.initialHash;
                    form.hasChanges = hasChanges;
                    return produce(state, draft => {
                        draft.forms[formId] = form;
                    });
                });
            },
            setFormField: <FieldId extends string, FieldValue>(
                fieldId: FieldId,
                update:
                    | FieldValue
                    | null
                    | undefined
                    | ((
                          currentValue: FieldValue | null | undefined,
                      ) => FieldValue | null | undefined),
            ) => {
                set(state => {
                    const output = produce(state, draft => {
                        const _form = draft.forms[formId] as TForm3<
                            FormSchema,
                            FormData,
                            string
                        >;
                        if (!_form) {
                            browserLogger.error(
                                `Form with id "${formId}" not found when trying to set field "${fieldId}"`,
                                {
                                    formId,
                                    fieldId,
                                    update: isFunction(update)
                                        ? "update is func"
                                        : JSON.stringify(update),
                                    stateForms: get().forms,
                                },
                            );
                            // eslint-disable-next-line no-console
                            console.warn(
                                `Form "${formId}" not found when trying to set field "${fieldId}"`,
                            );
                            return;
                        }

                        const initialValue = getFormValueAtPath<
                            FormData,
                            FieldId,
                            FieldValue
                        >(_form.initialData, fieldId) as
                            | FieldValue
                            | null
                            | undefined;

                        const currentValue = getFormValueAtPath<
                            FormData,
                            FieldId,
                            FieldValue
                        >(_form.data, fieldId) as FieldValue | null | undefined;

                        const newValue = isFunction(update)
                            ? update(currentValue)
                            : update;

                        const updatedForm = setFormValueAtPath<
                            FormData,
                            FieldId,
                            FieldValue | null | undefined
                        >(_form.data, fieldId, newValue);
                        _form.data = updatedForm;
                        const _hasChangedSinceLastTime =
                            newValue !== currentValue;
                        const hasChangedSinceTheBeginning =
                            currentValue !== initialValue;
                        browserLogger.info({
                            fieldId,
                            newValue,
                            currentValue,
                            hasChangedSinceTheBeginning,
                            isDirty: _form.dirty[fieldId],
                        });
                        _form.dirty[fieldId] =
                            hasChangedSinceTheBeginning || false;
                    });
                    return output;
                });
            },
            getFieldValue: <FieldId extends string, FieldValue>(
                fieldId: FieldId,
            ): FieldValue | null | undefined => {
                const form = get().getForm<FormSchema, FormData>(formId);
                if (!form) throw new Error(`Form with id ${formId} not found`);
                return getFormValueAtPath<FormData, FieldId, FieldValue>(
                    form.data,
                    fieldId,
                );
            },
            getFieldSchema: <FieldId extends string>(
                fieldId: FieldId,
            ): ZodSchema<unknown> | undefined => {
                const form = get().getForm<FormSchema, FormData>(formId);
                if (!form) throw new Error(`Form with id ${formId} not found`);
                const schema: ZodSchema<unknown> | undefined = getSchemaAtPath<
                    FormSchema,
                    FormData,
                    FieldId
                >(form.schema, fieldId);
                return schema;
            },
            validate: (): Form3ErrorMessage[] => {
                let errors: Form3ErrorMessage[] =
                    emptyArrayOf<Form3ErrorMessage>();
                set(state => {
                    return produce(state, draft => {
                        const form = draft.forms[formId];
                        if (!form)
                            throw new Error(`Form with id ${formId} not found`);
                        const validationResult = form.schema.safeParse(
                            form.data,
                        );
                        if (validationResult.success) {
                            browserLogger.info("validationResult hash");
                            draft.forms[formId].currentHash = hasher(form.data);
                            draft.forms[formId].hasChanges =
                                form.currentHash !== form.initialHash;
                            return;
                        }
                        errors = parseZodErrorMessages(validationResult.error);
                    });
                });
                return errors;
            },
            validateField: <FieldId extends string, FieldValue>(
                fieldId: FieldId,
                options: {
                    preProcessValue?: (
                        value: FieldValue | undefined | null,
                    ) => FieldValue | undefined;
                    customErrorMessage?: Form3ErrorMessage;
                } = {},
            ): Form3ErrorMessage[] => {
                let errors: Form3ErrorMessage[] =
                    emptyArrayOf<Form3ErrorMessage>();
                const form = get().getForm<FormSchema, FormData>(formId);
                if (!form) throw new Error(`Form with id ${formId} not found`);
                const fieldSchema = getSchemaAtPath<
                    FormSchema,
                    FormData,
                    FieldId
                >(form.schema, fieldId);

                if (!fieldSchema) {
                    throw new Error(
                        `Field "${String(fieldId)}" is not defined in the schema.`,
                    );
                }

                set(state => {
                    return produce(state, draft => {
                        const currentForm = draft.forms[formId] as TForm3<
                            FormSchema,
                            FormData,
                            string
                        >;
                        if (!currentForm)
                            throw new Error(`Form with id ${formId} not found`);
                        const fieldData = options?.preProcessValue
                            ? options.preProcessValue(
                                  currentForm.getFieldValue<FieldValue>(
                                      fieldId,
                                  ),
                              )
                            : currentForm.getFieldValue(fieldId);

                        const validationResult =
                            fieldSchema.safeParse(fieldData);
                        if (validationResult.success) {
                            currentForm.currentHash = hasher(currentForm.data);
                            currentForm.hasChanges =
                                currentForm.currentHash !==
                                currentForm.initialHash;
                            return;
                        }
                        errors = options?.customErrorMessage
                            ? [options.customErrorMessage]
                            : parseZodErrorMessages(validationResult.error);
                    });
                });
                browserLogger.info("validateField", errors);
                return errors;
            },
            resetToInitialState: (newForm?: FormData) => {
                set(state => {
                    return produce(state, draft => {
                        if (newForm) {
                            draft.forms[formId].data = newForm;
                            draft.forms[formId].initialData = newForm;
                            draft.forms[formId].currentHash = hasher(newForm);
                            draft.forms[formId].initialHash = hasher(newForm);
                            draft.forms[formId].hasChanges = false;
                        } else {
                            draft.forms[formId].data =
                                draft.forms[formId].initialData;
                            draft.forms[formId].currentHash =
                                draft.forms[formId].initialHash;
                            draft.forms[formId].hasChanges = false;
                        }
                    });
                });
            },
        };
        set(state => {
            return produce(state, draft => {
                (draft.forms[formId] as unknown) = form;
            });
        });
        return form;
    },
}));

export { useFormStore as useForm3Store };
export type { FormStore as Form3Store };
